import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadEventConfig } from 'pages/SchedulingConfigurations/store/thunk';
import {
  buildUnavailableBackgroundEvents,
  getUnavailableDayCellClassNames,
  isClickUnavailable
} from 'helpers/schedulingAvailability';

import 'helpers/schedulingAvailability.css';

const getRangeKey = (start, end) => {
  if(!start || !end) {
    return '';
  }

  return `${new Date(start).getTime()}-${new Date(end).getTime()}`;
};

const getEventsKey = events =>
  (events || []).map(event => `${event.id}:${event.start}:${event.end}`).join('|');

export default function useSchedulingAvailability() {
  const dispatch = useDispatch();
  const rangeRef = useRef({ start: null, end: null, key: '' });
  const eventsKeyRef = useRef('');

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { id, weeklyConfig, yearlyConfig } = useSelector(state => state.schedulingConfigurations);

  const [unavailableEvents, setUnavailableEvents] = useState([]);

  const refreshUnavailableEvents = useCallback((start, end) => {
    const rangeStart = start || rangeRef.current.start;
    const rangeEnd = end || rangeRef.current.end;
    const nextEvents = (!id || !rangeStart || !rangeEnd)
      ? []
      : buildUnavailableBackgroundEvents(weeklyConfig, yearlyConfig, rangeStart, rangeEnd);

    const nextKey = getEventsKey(nextEvents);

    if(nextKey === eventsKeyRef.current) {
      return;
    }

    eventsKeyRef.current = nextKey;
    setUnavailableEvents(nextEvents);
  }, [id, weeklyConfig, yearlyConfig]);

  useEffect(() => {
    if(userId && establishmentId) {
      dispatch(loadEventConfig(userId, establishmentId, { silenceErrorToast: true }));
    }
  }, [dispatch, userId, establishmentId]);

  useEffect(() => {
    refreshUnavailableEvents();
  }, [refreshUnavailableEvents]);

  const handleDatesSet = useCallback(({ start, end }) => {
    const nextKey = getRangeKey(start, end);

    if(nextKey && nextKey === rangeRef.current.key) {
      return;
    }

    rangeRef.current = { start, end, key: nextKey };
    refreshUnavailableEvents(start, end);
  }, [refreshUnavailableEvents]);

  const dayCellClassNames = useCallback(arg => {
    if(!id) {
      return [];
    }

    return getUnavailableDayCellClassNames(arg.date, weeklyConfig, yearlyConfig);
  }, [id, weeklyConfig, yearlyConfig]);

  const isDateUnavailable = useCallback((date, allDay = false) => {
    if(!id) {
      return false;
    }

    return isClickUnavailable(date, allDay, weeklyConfig, yearlyConfig);
  }, [id, weeklyConfig, yearlyConfig]);

  return {
    unavailableEvents,
    handleDatesSet,
    dayCellClassNames,
    isDateUnavailable
  };
}
